import formatPrice from '@honda-canada/js-utilities/lib/formatPrice';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Carousel, Copy, H5, Icon, Image, Optional, StatusBadge, useCarouselControl, } from '@honda-canada/design-system-react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Context from './service/Context';
import gtmLearnMoreCarouselClick from '../../core/gtm/gtmLearnMoreCarouselClick';
import themeStyles from './AccessoryDetailModal.styles';
import themeStylesBap from './ConfigureBapStyles';
import { progressBarSections } from './hooks/useVisibleSections';
import { selectBodyTypesForGtm, selectCurrentVehicleFromVehicles, selectTransmissionFromCurrentVehicle, } from '../../core/reducers/vehicles';
import { selectCurrentVehicle } from '../../core/reducers/currentVehicle';
import { selectPrimaryCalculatorIsPaymentMethodCash, selectPrimaryCalculatorPaymentFrequency, } from '../../core/reducers/financialCalculators';
import { setActiveProgressBarItem, setCurrentAccessoryModalId } from '../../core/reducers/ui';
import { useAccessoriesFetch } from '../../core/apiHooks/useAccessoriesFetch';
import { useAppSelector } from '../../core/utils';
import { useFetchCurrentVehiclePayment } from '../../core/apiHooks/useFetchPayment';
import { useLanguage } from '../../utils/sitecoreContext';
const Container = themeStyles.apply(Box, 'Container');
const CloseButton = themeStyles.apply(Box, 'CloseButton');
const CloseIconWrapper = themeStyles.apply(Box, 'CloseIconWrapper');
const ImageContainer = themeStyles.apply(Box, 'ImageContainer');
const Title = themeStyles.apply(H5, 'Title');
const Description = themeStyles.apply(Copy, 'Description');
const PriceContainer = themeStyles.apply(Box, 'PriceContainer');
const PaymentFrequency = themeStylesBap.apply(Copy, 'PaymentFrequency');
const CarouselImage = themeStyles.apply(Image, 'CarouselImage');
const CarouselWrapper = themeStyles.apply(Box, 'CarouselWrapper');
const CAROUSEL_ARROW_STYLES = {
    marginBottom: ['zero', 'zero', '-85px'],
    bottom: 'zero',
    top: 'auto !important',
    alignItems: 'flex-end',
    zIndex: '2',
};
const AccessoryDetailsModal = () => {
    const closeButtonRef = useRef(null);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const language = useLanguage();
    const { setScrollTrigger } = useContext(Context);
    const [imageError, setImageError] = useState(false);
    const gtmBodyStyle = useAppSelector(selectBodyTypesForGtm);
    const { inventoryStatus } = useAppSelector(selectCurrentVehicle);
    const currentVehicle = useAppSelector(selectCurrentVehicleFromVehicles);
    const currentTransmission = useAppSelector(selectTransmissionFromCurrentVehicle);
    const { currentAccessoryModalId } = useAppSelector(state => state.ui.ConfigureBap);
    const currentPaymentMethodCash = useAppSelector(selectPrimaryCalculatorIsPaymentMethodCash);
    const paymentFrequencyLabel = useAppSelector(selectPrimaryCalculatorPaymentFrequency);
    const { data: paymentData } = useFetchCurrentVehiclePayment();
    const { data } = useAccessoriesFetch((currentTransmission === null || currentTransmission === void 0 ? void 0 : currentTransmission.detIdentifier) || '');
    const selectedAccessory = {
        ...data === null || data === void 0 ? void 0 : data.reduce((acc, accessory) => {
            var _a;
            if (accessory.businessKey === currentAccessoryModalId) {
                return {
                    ...accessory,
                    payment: (_a = paymentData === null || paymentData === void 0 ? void 0 : paymentData.accessories.find(access => access.key === accessory.businessKey)) === null || _a === void 0 ? void 0 : _a.frequencyPayment,
                };
            }
            return acc;
        }, {}),
    };
    const { isIncluded } = selectedAccessory;
    const paymentFrequency = t(`Shared.Common.${paymentFrequencyLabel}OptionLabel`);
    const includedLabel = t('Pages.Bap.includedLabel');
    useEffect(() => {
        if (closeButtonRef.current) {
            closeButtonRef.current.scrollIntoView({ block: 'center' });
        }
    }, []);
    const handleCloseDetailsModal = () => dispatch(setCurrentAccessoryModalId(''));
    useEffect(() => {
        var _a, _b;
        if (selectedAccessory === null || selectedAccessory === void 0 ? void 0 : selectedAccessory.category) {
            const categoryId = (_b = (_a = selectedAccessory === null || selectedAccessory === void 0 ? void 0 : selectedAccessory.category) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '';
            if (progressBarSections.includes(categoryId))
                dispatch(setActiveProgressBarItem(categoryId));
        }
        return () => {
            handleCloseDetailsModal();
            setScrollTrigger(true);
        };
    }, []);
    const accessoryImages = Array.isArray(selectedAccessory.additionalThumbnailAssetUrls)
        ? selectedAccessory.additionalThumbnailAssetUrls.map((imageUrl) => (React.createElement(CarouselImage, { key: `accessory-carousel-image-${imageUrl}`, src: imageUrl, alt: selectedAccessory.name, "data-testid": "cy-bap-accessory-details-image", onError: () => setImageError(true) })))
        : [];
    const behavior = useCarouselControl(accessoryImages === null || accessoryImages === void 0 ? void 0 : accessoryImages.length, false);
    const { currentSlide, pages } = behavior;
    return (React.createElement(Container, { "data-testid": "cy-accessory-details-modal" },
        React.createElement(CloseButton, { ref: closeButtonRef, onClick: handleCloseDetailsModal, "data-testid": "cy-accessory-details-modal-close-button" },
            React.createElement(CloseIconWrapper, null,
                React.createElement(Icon, { name: "close", color: "black" }))),
        React.createElement(Optional, { when: imageError || !selectedAccessory.thumbnailAssetUrl },
            React.createElement(ImageContainer, null,
                React.createElement(Image, { src: selectedAccessory.defaultThumbnailAssetUrl, alt: selectedAccessory.name, "data-testid": "cy-bap-accessory-details-no-image" }))),
        React.createElement(Optional, { when: !imageError && selectedAccessory.thumbnailAssetUrl },
            React.createElement(CarouselWrapper, null,
                React.createElement(Carousel, { fullWidthImage: true, "data-testid": "cy-accessory-image-carousel", items: accessoryImages, arrowSize: "default", contentHeight: "100%", onClick: (buttonId) => gtmLearnMoreCarouselClick({
                        gtmBodyStyle,
                        model: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.modelGtmName,
                        trim: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.trimGtmName,
                        availabilityFlag: inventoryStatus,
                        productName: selectedAccessory.name,
                        action: buttonId === 'arrowRight' ? 'right' : 'left',
                    }), behavior: behavior, leftArrowColumnStyle: { ...CAROUSEL_ARROW_STYLES, opacity: currentSlide === 0 ? '0.3' : '1' }, rightArrowColumnStyle: { ...CAROUSEL_ARROW_STYLES, opacity: currentSlide === pages - 1 ? '0.3' : '1' }, styling: "black" }))),
        React.createElement(Title, { "data-testid": "cy-accessory-details-modal-title" }, selectedAccessory.name),
        React.createElement(PriceContainer, null,
            React.createElement(Optional, { when: !isIncluded },
                React.createElement(Copy, { size: "pricingSmall", "data-testid": "cy-accessory-details-modal-price" }, formatPrice(selectedAccessory.provincialPrice, language, 2)),
                React.createElement(Optional, { when: !currentPaymentMethodCash },
                    React.createElement(PaymentFrequency, { size: "frequency", "data-testid": "cy-accessory-details-modal-payment" },
                        formatPrice(selectedAccessory.payment, language, 2),
                        "/",
                        paymentFrequency))),
            React.createElement(Optional, { when: isIncluded },
                React.createElement(StatusBadge, { statusIconName: "selected", statusText: includedLabel }))),
        React.createElement(Description, { "data-testid": "cy-accessory-details-modal-description" }, selectedAccessory.description)));
};
export default AccessoryDetailsModal;
